import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExploreRoundedIcon from '@mui/icons-material/ExploreRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import { CircleMarker, MapContainer, Marker, TileLayer, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import InventoryImage from '../components/InventoryImage';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const MAP_DEFAULT_CENTER = [39.8283, -98.5795];
const MAP_DEFAULT_ZOOM = 4;
const LOCATION_GROUPING_DISTANCE_METERS = 20;

const sectionSurfaceSx = {
  borderRadius: 4,
  border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
  background: (theme) =>
    `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
      theme.palette.background.paper,
      0.98,
    )} 52%, ${alpha(theme.palette.info.light, 0.08)} 100%)`,
  boxShadow: (theme) => `0 18px 48px ${alpha(theme.palette.common.black, 0.08)}`,
};

const explorerFilterControlSx = {
  flex: {
    xs: '1 1 100%',
    sm: '1 1 calc(50% - 12px)',
    lg: '1 1 220px',
  },
  minWidth: 0,
};

const explorerToggleButtonSx = {
  gap: 0.75,
  px: { xs: 1.25, sm: 1.5 },
  minWidth: 0,
  whiteSpace: 'nowrap',
  '& .MuiTypography-root': {
    lineHeight: 1,
  },
};

function SignalCard({ eyebrow, title, value, helper, icon, accent = 'primary.main' }) {
  return (
    <Card sx={{ ...sectionSurfaceSx, height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
            <Box>
              <Typography variant="overline" color="text.secondary">
                {eyebrow}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '16px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                color: accent,
              }}
            >
              {icon}
            </Box>
          </Stack>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
              {helper}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function formatCoordinate(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'N/A';
  }
  return value.toFixed(4);
}

function formatLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatPartnerRelationship(value) {
  if (value === 'child') {
    return 'Business Unit';
  }
  return formatLabel(value);
}

function getItemProperties(item) {
  return item?.properties && typeof item.properties === 'object' ? item.properties : {};
}

function isExplorerListed(item) {
  const properties = getItemProperties(item);
  return properties.list_on_capabilities_explorer ??
    properties.listed_on_capabilities_explorer ??
    properties.is_listed ??
    Boolean(item?.global_response);
}

function getNeedCategoryKey(item) {
  return item.category_id || item.category_name || item.category_display_name || '';
}

function getPartnerRelationship(item, memberPartnerIds, childPartnerIds) {
  if (memberPartnerIds.includes(item.partner_id)) {
    return 'owned';
  }
  if (childPartnerIds.includes(item.partner_id)) {
    return 'child';
  }
  return 'external';
}

function getGroupingStep(zoom) {
  if (zoom <= 4) {
    return 3;
  }

  if (zoom <= 5) {
    return 2;
  }

  if (zoom <= 6) {
    return 1;
  }

  if (zoom <= 7) {
    return 0.5;
  }

  if (zoom <= 8) {
    return 0.2;
  }

  return 0;
}

function getDistanceBetweenCoordinates(latitudeA, longitudeA, latitudeB, longitudeB) {
  return L.latLng(latitudeA, longitudeA).distanceTo([latitudeB, longitudeB]);
}

function appendCapabilityToGroup(group, capability, latitude, longitude) {
  const nextCapabilityCount = group.capabilities.length + 1;
  group.latitude = (group.latitude * group.capabilities.length + latitude) / nextCapabilityCount;
  group.longitude = (group.longitude * group.capabilities.length + longitude) / nextCapabilityCount;
  group.capabilities.push(capability);
}

function mergeLocationGroups(groups) {
  const mergedGroups = [...groups];
  let didMerge = true;

  while (didMerge) {
    didMerge = false;

    for (let leftIndex = 0; leftIndex < mergedGroups.length; leftIndex += 1) {
      const leftGroup = mergedGroups[leftIndex];

      for (let rightIndex = leftIndex + 1; rightIndex < mergedGroups.length; rightIndex += 1) {
        const rightGroup = mergedGroups[rightIndex];
        const distance = getDistanceBetweenCoordinates(
          leftGroup.latitude,
          leftGroup.longitude,
          rightGroup.latitude,
          rightGroup.longitude,
        );

        if (distance > LOCATION_GROUPING_DISTANCE_METERS) {
          continue;
        }

        rightGroup.capabilities.forEach((capability) => {
          appendCapabilityToGroup(
            leftGroup,
            capability,
            Number(capability.latitude),
            Number(capability.longitude),
          );
        });
        mergedGroups.splice(rightIndex, 1);
        didMerge = true;
        break;
      }

      if (didMerge) {
        break;
      }
    }
  }

  return mergedGroups;
}

function CapabilityMapViewport({ bounds, boundsSignature, selectedLocation }) {
  const map = useMap();
  const hasInitializedRef = useRef(false);
  const previousBoundsSignatureRef = useRef('');
  const previousSelectedKeyRef = useRef('');

  useEffect(() => {
    if (selectedLocation) {
      const selectedKey = selectedLocation.key;

      if (previousSelectedKeyRef.current !== selectedKey) {
        map.panInside([selectedLocation.latitude, selectedLocation.longitude], {
          paddingTopLeft: [32, 32],
          paddingBottomRight: [460, 32],
          animate: true,
        });
      }

      previousSelectedKeyRef.current = selectedKey;
      return;
    }

    previousSelectedKeyRef.current = '';

    const shouldFitBounds =
      Boolean(bounds) &&
      (!hasInitializedRef.current || previousBoundsSignatureRef.current !== boundsSignature);

    if (shouldFitBounds) {
      map.fitBounds(bounds, {
        padding: [32, 32],
        maxZoom: 7,
      });
      hasInitializedRef.current = true;
      previousBoundsSignatureRef.current = boundsSignature;
      return;
    }

    if (!bounds && !hasInitializedRef.current) {
      map.setView(MAP_DEFAULT_CENTER, MAP_DEFAULT_ZOOM);
      hasInitializedRef.current = true;
    }

    previousBoundsSignatureRef.current = boundsSignature;
  }, [bounds, boundsSignature, map, selectedLocation]);

  return null;
}

function CapabilityMapResizer({ selectedLocationKey }) {
  const map = useMap();

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      map.invalidateSize();
    }, 150);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [map, selectedLocationKey]);

  return null;
}

function CapabilityMapMarkers({ groups, selectedLocationKey, onSelectLocation }) {
  const map = useMapEvents({
    zoomend: () => {
      setZoom(map.getZoom());
    },
  });
  const [zoom, setZoom] = useState(() => map.getZoom());

  const displayGroups = useMemo(() => {
    const groupingStep = getGroupingStep(zoom);

    if (!groupingStep) {
      return groups.map((group) => ({
        key: group.key,
        latitude: group.latitude,
        longitude: group.longitude,
        capabilities: group.capabilities,
        childLocations: [group],
        isAggregate: false,
      }));
    }

    const aggregatedGroups = new Map();

    groups.forEach((group) => {
      const latBucket = Math.round(group.latitude / groupingStep);
      const lonBucket = Math.round(group.longitude / groupingStep);
      const bucketKey = `${groupingStep}:${latBucket}:${lonBucket}`;

      if (!aggregatedGroups.has(bucketKey)) {
        aggregatedGroups.set(bucketKey, {
          key: bucketKey,
          totalWeight: 0,
          weightedLatitude: 0,
          weightedLongitude: 0,
          capabilities: [],
          childLocations: [],
        });
      }

      const bucket = aggregatedGroups.get(bucketKey);
      const weight = group.capabilities.length;

      bucket.totalWeight += weight;
      bucket.weightedLatitude += group.latitude * weight;
      bucket.weightedLongitude += group.longitude * weight;
      bucket.capabilities.push(...group.capabilities);
      bucket.childLocations.push(group);
    });

    return Array.from(aggregatedGroups.values())
      .map((group) => ({
        key: group.key,
        latitude: group.weightedLatitude / group.totalWeight,
        longitude: group.weightedLongitude / group.totalWeight,
        capabilities: group.capabilities,
        childLocations: group.childLocations,
        isAggregate: group.childLocations.length > 1,
      }))
      .sort((left, right) => right.capabilities.length - left.capabilities.length);
  }, [groups, zoom]);

  return displayGroups.map((group) => {
    const childLocation = group.childLocations[0];
    const isSelected =
      !group.isAggregate &&
      childLocation &&
      childLocation.key === selectedLocationKey;

    const handleClick = () => {
      if (group.isAggregate) {
        map.flyTo([group.latitude, group.longitude], Math.min(map.getZoom() + 4, 12), {
          duration: 0.45,
        });
        return;
      }

      onSelectLocation(childLocation.key);
    };

    if (group.isAggregate) {
      const clusterCount = group.childLocations.length;
      const clusterDiameter = Math.min(56, 34 + clusterCount * 3);
      const clusterIcon = L.divIcon({
        className: 'capability-map-cluster-icon',
        html: `<div style="width:${clusterDiameter}px;height:${clusterDiameter}px;border-radius:999px;background:#155eef;border:3px solid #ffffff;box-shadow:0 10px 24px rgba(21,94,239,0.28);display:flex;align-items:center;justify-content:center;color:#ffffff;font-weight:800;font-size:${Math.max(14, Math.min(18, 12 + String(clusterCount).length))}px;line-height:1;">${clusterCount}</div>`,
        iconSize: [clusterDiameter, clusterDiameter],
        iconAnchor: [clusterDiameter / 2, clusterDiameter / 2],
      });

      return (
        <Marker
          key={group.key}
          position={[group.latitude, group.longitude]}
          icon={clusterIcon}
          eventHandlers={{ click: handleClick }}
        >
          <Tooltip direction="top" offset={[0, -12]}>
            {`${clusterCount} nearby locations, ${group.capabilities.length} capabilities`}
          </Tooltip>
        </Marker>
      );
    }

    return (
      <CircleMarker
        key={group.key}
        center={[group.latitude, group.longitude]}
        radius={7}
        eventHandlers={{ click: handleClick }}
        pathOptions={{
          color: isSelected ? '#b42318' : '#0b6bcb',
          fillColor: isSelected ? '#f04438' : '#0b6bcb',
          fillOpacity: 0.84,
          weight: isSelected ? 3 : 1,
        }}
      >
        <Tooltip direction="top" offset={[0, -8]}>
          {`${group.capabilities.length} ${group.capabilities.length === 1 ? 'capability' : 'capabilities'} at this location`}
        </Tooltip>
      </CircleMarker>
    );
  });
}

function CapabilityResultCard({ item, onRequestTarget }) {
  const title = item.capability_name;
  const canRequestCapability = Boolean(
    item.capability_definition_id &&
      item.capability_status === 'active' &&
      item.capability_allow_direct_requests,
  );

  return (
    <Card elevation={0} sx={sectionSurfaceSx}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <InventoryImage
            fileId={item.capability_image_id}
            alt={title || 'Capability image'}
            height={132}
            sx={{ width: { xs: '100%', md: 180 }, flexShrink: 0 }}
          />
          <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                Capability
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                {title || 'Unnamed Capability'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.partner_code || item.partner_id || 'Partner unavailable'}
              </Typography>
              {item.location_address || item.location_label ? (
                <Typography variant="body2" color="text.secondary">
                  {item.location_address || item.location_label}
                </Typography>
              ) : null}
            </Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={item.listing_visible ? 'Listed' : 'Unlisted'} color={item.listing_visible ? 'success' : 'default'} />
              <Chip size="small" label={formatPartnerRelationship(item.partner_relationship)} variant="outlined" />
              <Chip size="small" label={item.category_display_name || item.category_name || 'Uncategorized'} variant="outlined" />
              <Chip size="small" label={formatLabel(item.status_filter)} variant="outlined" />
            </Stack>
            {item.properties?.description ? (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {item.properties.description}
              </Typography>
            ) : null}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {canRequestCapability ? (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SendRoundedIcon />}
                  onClick={() => onRequestTarget(item)}
                >
                  Request Capability
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function CapabilitiesExplorer(props) {
  const navigate = useNavigate();
  const [capabilityData, setCapabilityData] = useState({
    filters: {},
    capabilities: [],
  });
  const [capabilityLoading, setCapabilityLoading] = useState(true);
  const [userDataLoading, setUserDataLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPartnerCodes, setSelectedPartnerCodes] = useState([]);
  const [selectedNeedCategories, setSelectedNeedCategories] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedRelationships, setSelectedRelationships] = useState([]);
  const [locationQuery, setLocationQuery] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [selectedLocationKey, setSelectedLocationKey] = useState('');
  const [userMemberships, setUserMemberships] = useState([]);
  const [userChildMemberships, setUserChildMemberships] = useState([]);

  const capabilityItems = useMemo(
    () => capabilityData.capabilities.map((capability) => ({
      ...capability,
      listing_visible: isExplorerListed(capability),
      partner_relationship: getPartnerRelationship(capability, userMemberships, userChildMemberships),
      status_filter: capability.properties?.availability === false ? 'unavailable' : 'available',
      capability_status: capability.status,
      capability_allow_direct_requests: capability.allow_direct_requests,
    })),
    [capabilityData.capabilities, userChildMemberships, userMemberships],
  );

  const filterCapability = useCallback((item) => {
    if (!item.listing_visible) {
      return false;
    }

    if (selectedPartnerCodes.length && !selectedPartnerCodes.includes(item.partner_code)) {
      return false;
    }

    if (
      selectedNeedCategories.length &&
      !selectedNeedCategories.includes(getNeedCategoryKey(item))
    ) {
      return false;
    }

    if (selectedRelationships.length && !selectedRelationships.includes(item.partner_relationship)) {
      return false;
    }

    const normalizedLocationQuery = locationQuery.trim().toLowerCase();
    if (normalizedLocationQuery) {
      const locationValues = [
        item.location_address,
        item.location_label,
        item.facility_id,
        item.business_unit_display_name,
        item.business_unit_code,
        item.partner_display_name,
        item.partner_code,
      ];
      if (!locationValues.some((value) => String(value || '').toLowerCase().includes(normalizedLocationQuery))) {
        return false;
      }
    }

    return true;
  }, [locationQuery, selectedNeedCategories, selectedPartnerCodes, selectedRelationships]);

  const filteredCapabilities = useMemo(
    () => capabilityItems.filter((capability) => {
      if (!filterCapability(capability)) {
        return false;
      }
      if (selectedStatuses.length && !selectedStatuses.includes(capability.status_filter)) {
        return false;
      }
      return true;
    }),
    [capabilityItems, filterCapability, selectedStatuses],
  );

  const mappedItems = useMemo(
    () => filteredCapabilities.filter((item) => {
      const latitude = Number(item.latitude);
      const longitude = Number(item.longitude);
      return item.latitude !== null && item.latitude !== ''
        && item.longitude !== null && item.longitude !== ''
        && Number.isFinite(latitude) && Number.isFinite(longitude);
    }),
    [filteredCapabilities],
  );

  const needCategoryOptions = useMemo(
    () => Array.from(
      capabilityItems.reduce((categories, item) => {
        const value = getNeedCategoryKey(item);
        if (value && !categories.has(value)) {
          categories.set(value, item.category_display_name || item.category_name || value);
        }
        return categories;
      }, new Map()),
      ([value, label]) => ({ value, label }),
    ).sort((left, right) => left.label.localeCompare(right.label)),
    [capabilityItems],
  );

  const partnerOptions = useMemo(
    () =>
      Array.from(
        new Set(capabilityItems.map((item) => item.partner_code).filter(Boolean)),
      ).sort((left, right) => left.localeCompare(right)),
    [capabilityItems],
  );

  const statusOptions = useMemo(
    () =>
      Array.from(
        new Set(capabilityItems.map((item) => item.status_filter).filter(Boolean)),
      ).sort((left, right) => left.localeCompare(right)),
    [capabilityItems],
  );

  const capabilityMapGroups = useMemo(() => {
    const groups = [];

    mappedItems.forEach((item) => {
      const latitude = Number(item.latitude);
      const longitude = Number(item.longitude);

      const matchingGroup = groups.find(
        (group) =>
          getDistanceBetweenCoordinates(
            latitude,
            longitude,
            group.latitude,
            group.longitude,
          ) <= LOCATION_GROUPING_DISTANCE_METERS,
      );

      if (!matchingGroup) {
        groups.push({
          latitude,
          longitude,
          capabilities: [item],
        });
        return;
      }

      appendCapabilityToGroup(matchingGroup, item, latitude, longitude);
    });

    return mergeLocationGroups(groups)
      .map((group) => ({
        ...group,
        key: group.capabilities
          .map((item) => item.capability_definition_id || item.capability_name)
          .sort((left, right) => String(left).localeCompare(String(right)))
          .join('|'),
      }))
      .sort((left, right) => right.capabilities.length - left.capabilities.length);
  }, [mappedItems]);

  const capabilityMapBounds = useMemo(() => {
    if (!capabilityMapGroups.length) {
      return null;
    }

    return L.latLngBounds(
      capabilityMapGroups.map((group) => [group.latitude, group.longitude]),
    );
  }, [capabilityMapGroups]);
  const capabilityMapBoundsSignature = useMemo(
    () => capabilityMapGroups.map((group) => group.key).join('|'),
    [capabilityMapGroups],
  );

  const selectedLocation = useMemo(
    () => capabilityMapGroups.find((group) => group.key === selectedLocationKey) || null,
    [capabilityMapGroups, selectedLocationKey],
  );
  const selectedLocationCapabilityCount = selectedLocation
    ? selectedLocation.capabilities.length
    : 0;
  const hasActiveFilters =
    selectedPartnerCodes.length > 0 ||
    selectedNeedCategories.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedRelationships.length > 0 ||
    Boolean(locationQuery.trim());

  useEffect(() => {
    let isMounted = true;

    async function fetchCapabilities() {
      setCapabilityLoading(true);
      setError('');

      try {
        const res = await axiosInstance.get('/capabilities');
        const apiData = res?.data ?? {};

        if (!isMounted) {
          return;
        }

        setCapabilityData({
          filters: apiData.filters ?? {},
          capabilities: Array.isArray(apiData.capabilities) ? apiData.capabilities : [],
        });

      } catch (err) {
        console.error('Failed to fetch capabilities', {
          message: err?.message,
          status: err?.response?.status,
          data: err?.response?.data,
          url: err?.config?.url,
          baseURL: err?.config?.baseURL,
        });

        if (!isMounted) {
          return;
        }

        setCapabilityData({
          filters: {},
          capabilities: [],
        });
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to fetch capabilities database.',
        );
      } finally {
        if (isMounted) {
          setCapabilityLoading(false);
        }
      }
    }

    fetchCapabilities();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function fetchUserPartnerRelationships() {
      setUserDataLoading(true);
      setError('');

      function findAllChildren(parentId, partners) {
        const queue = [parentId];
        const children = [];
        const seen = new Set();

        while (queue.length) {
          const currentId = queue.shift();
          partners
            .filter((partner) => partner.parent_partner_id && partner.parent_partner_id === currentId)
            .forEach((partner) => {
              if (seen.has(partner.partner_id)) {
                return;
              }
              seen.add(partner.partner_id);
              children.push(partner.partner_id);
              queue.push(partner.partner_id);
            });
        }
        return children;
      }

      try {
        const [userResponse, catalogResponse] = await Promise.all([
          axiosInstance.get('/partners/user'),
          axiosInstance.get('/partners/user/catalog'),
        ]);
        const memberships = Array.isArray(userResponse?.data?.memberships)
          ? userResponse.data.memberships.map((item) => item.partner_id).filter(Boolean)
          : [];
        const partners = Array.isArray(catalogResponse?.data?.partners)
          ? catalogResponse.data.partners
          : [];
        const childIds = memberships.flatMap((partnerId) => findAllChildren(partnerId, partners));

        if (!isMounted) {
          return;
        }

        setUserMemberships(memberships);
        setUserChildMemberships(Array.from(new Set(childIds)));
      } catch (err) {
        console.error('Failed to fetch user data', {
          message: err?.message,
          status: err?.response?.status,
          data: err?.response?.data,
          url: err?.config?.url,
          baseURL: err?.config?.baseURL,
        });

        if (!isMounted) return;

        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to fetch user data.',
        );
      } finally {
        if (isMounted) {
          setUserDataLoading(false);
        }
      }
    }

    fetchUserPartnerRelationships();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleRequestTarget = (target) => {
    const targetId = target.capability_definition_id;
    if (!targetId) {
      setError('This capability is missing an AMX identifier.');
      return;
    }

    navigate(`/capabilities/${encodeURIComponent(targetId)}/request`);
  };

  useEffect(() => {
    if (!selectedLocationKey) {
      return;
    }

    const selectedStillVisible = capabilityMapGroups.some(
      (group) => group.key === selectedLocationKey,
    );

    if (!selectedStillVisible) {
      setSelectedLocationKey('');
    }
  }, [capabilityMapGroups, selectedLocationKey]);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            minWidth: 0,
            p: { xs: 2, sm: 3 },
            pt: { xs: 10, md: 3 },
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflowX: 'hidden',
          }}
        >
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Capabilities Explorer' },
            ]}
          />

          <Stack spacing={3} sx={{ flexGrow: 1, minHeight: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                  xl: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 3,
              }}
            >
              <SignalCard
                eyebrow="Registry"
                title="Capabilities"
                value={capabilityData.capabilities.length}
                helper="Distinct capability definitions currently available in the federated registry."
                icon={<HubRoundedIcon />}
              />
              <SignalCard
                eyebrow="Geography"
                title="Mapped Capabilities"
                value={mappedItems.length}
                helper="Capabilities with valid coordinates that can be rendered directly on the map."
                icon={<MapRoundedIcon />}
                accent="info.main"
              />
              <SignalCard
                eyebrow="Coverage"
                title="Locations"
                value={capabilityMapGroups.length}
                helper="Grouped geographic points where one or more capabilities are available."
                icon={<PlaceRoundedIcon />}
                accent="success.main"
              />
              <SignalCard
                eyebrow="Focus"
                title="Visible Capabilities"
                value={filteredCapabilities.length}
                helper="Capabilities remaining after the current filters are applied."
                icon={<ExploreRoundedIcon />}
                accent="warning.main"
              />
            </Box>

            <Card sx={sectionSurfaceSx}>
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Filter the capability footprint
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Filter capabilities by need category, provider, status, relationship, or location.
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        color={filteredCapabilities.length ? 'success' : 'default'}
                        label={`${filteredCapabilities.length} visible capabilities`}
                      />
                      <Chip
                        variant="outlined"
                        label={`${capabilityMapGroups.length} visible locations`}
                      />
                      {hasActiveFilters ? (
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<FilterAltRoundedIcon />}
                          onClick={() => {
                            setSelectedPartnerCodes([]);
                            setSelectedNeedCategories([]);
                            setSelectedStatuses([]);
                            setSelectedRelationships([]);
                            setLocationQuery('');
                          }}
                        >
                          Clear filters
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ minWidth: 0 }}
                  >
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={viewMode}
                      onChange={(event, nextValue) => {
                        if (nextValue) {
                          setViewMode(nextValue);
                        }
                      }}
                      sx={{
                        flex: { xs: '1 1 100%', sm: '0 0 auto' },
                        minWidth: 0,
                        '& .MuiToggleButton-root': {
                          flex: { xs: 1, sm: '0 0 auto' },
                        },
                      }}
                    >
                      <ToggleButton value="map" aria-label="Map" sx={explorerToggleButtonSx}>
                        <MapRoundedIcon fontSize="small" />
                        <Typography component="span" variant="caption" sx={{ fontWeight: 700 }}>
                          Map
                        </Typography>
                      </ToggleButton>
                      <ToggleButton value="list" aria-label="List" sx={explorerToggleButtonSx}>
                        <ViewListRoundedIcon fontSize="small" />
                        <Typography component="span" variant="caption" sx={{ fontWeight: 700 }}>
                          List
                        </Typography>
                      </ToggleButton>
                    </ToggleButtonGroup>

                    <TextField
                      label="Search Location"
                      value={locationQuery}
                      onChange={(event) => setLocationQuery(event.target.value)}
                      placeholder="Address, city, state, postal code, or provider"
                      sx={explorerFilterControlSx}
                    />

                    <FormControl size="medium" sx={explorerFilterControlSx}>
                      <InputLabel id="partner-filter-label">Partners</InputLabel>
                      <Select
                        labelId="partner-filter-label"
                        multiple
                        value={selectedPartnerCodes}
                        onChange={(event) => {
                          setSelectedPartnerCodes(event.target.value);
                        }}
                        input={<OutlinedInput label="Partners" />}
                        renderValue={(selected) =>
                          selected.length ? selected.join(', ') : 'All partners'
                        }
                      >

                        {partnerOptions
                        .map((partnerCode) => (
                          <MenuItem key={partnerCode} value={partnerCode}>
                            <Checkbox checked={selectedPartnerCodes.includes(partnerCode)} />
                            <ListItemText primary={partnerCode} />
                          </MenuItem>
                        ))
                        }
                      </Select>
                    </FormControl>
                    

                    <FormControl size="medium" sx={explorerFilterControlSx}>
                      <InputLabel id="need-category-filter-label">Need Category</InputLabel>
                      <Select
                        labelId="need-category-filter-label"
                        multiple
                        value={selectedNeedCategories}
                        onChange={(event) =>
                          setSelectedNeedCategories(event.target.value)
                        }
                        input={<OutlinedInput label="Need Category" />}
                        renderValue={(selected) =>
                          selected.length
                            ? selected.map((value) => needCategoryOptions.find((option) => option.value === value)?.label || value).join(', ')
                            : 'All need categories'
                        }
                      >
                        {needCategoryOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Checkbox
                              checked={selectedNeedCategories.includes(option.value)}
                            />
                            <ListItemText primary={option.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="medium" sx={explorerFilterControlSx}>
                      <InputLabel id="status-filter-label">Status</InputLabel>
                      <Select
                        labelId="status-filter-label"
                        multiple
                        value={selectedStatuses}
                        onChange={(event) => setSelectedStatuses(event.target.value)}
                        input={<OutlinedInput label="Status" />}
                        renderValue={(selected) =>
                          selected.length ? selected.map(formatLabel).join(', ') : 'All statuses'
                        }
                      >
                        {statusOptions.map((status) => (
                          <MenuItem key={status} value={status}>
                            <Checkbox checked={selectedStatuses.includes(status)} />
                            <ListItemText primary={formatLabel(status)} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="medium" sx={explorerFilterControlSx}>
                      <InputLabel id="relationship-filter-label">Relationship</InputLabel>
                      <Select
                        labelId="relationship-filter-label"
                        multiple
                        value={selectedRelationships}
                        onChange={(event) => setSelectedRelationships(event.target.value)}
                        input={<OutlinedInput label="Relationship" />}
                        renderValue={(selected) =>
                          selected.length ? selected.map(formatPartnerRelationship).join(', ') : 'All relationships'
                        }
                      >
                        {['owned', 'child', 'external'].map((relationship) => (
                          <MenuItem key={relationship} value={relationship}>
                            <Checkbox checked={selectedRelationships.includes(relationship)} />
                            <ListItemText primary={formatPartnerRelationship(relationship)} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {viewMode === 'map' ? (
                      <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        flex: { xs: '1 1 100%', lg: '1 1 260px' },
                        minWidth: 0,
                        borderRadius: 3,
                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.65),
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '14px',
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                            color: 'primary.main',
                          }}
                        >
                          <ExploreRoundedIcon />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Map guidance
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Click a bubble to open the capabilities at that site.
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                    ) : null}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {capabilityLoading || userDataLoading ? (
              <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                <CircularProgress />
                <Typography color="text.secondary">Loading capabilities explorer...</Typography>
              </Stack>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : viewMode === 'list' ? (
              filteredCapabilities.length ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                  }}
                >
                  {filteredCapabilities.map((item) => (
                    <CapabilityResultCard
                      key={item.capability_definition_id}
                      item={item}
                      onRequestTarget={handleRequestTarget}
                    />
                  ))}
                </Box>
              ) : (
                <Alert severity="info">
                  No capabilities match the active filters.
                </Alert>
              )
            ) : !mappedItems.length ? (
              <Alert severity="info">
                No capability coordinates match the active filters. Add latitude and longitude
                to a capability to show it on the map.
              </Alert>
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  flexGrow: 1,
                  minWidth: 0,
                  height: {
                    xs: 'min(680px, calc(100vh - 120px))',
                    md: 'min(760px, calc(100vh - 160px))',
                    xl: 'calc(100vh - 220px)',
                  },
                  minHeight: { xs: 420, sm: 500, lg: 560 },
                  maxHeight: { xs: 'none', xl: 820 },
                  borderRadius: 5,
                  overflow: 'hidden',
                  border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                  boxShadow: '0 24px 54px rgba(15, 23, 42, 0.14)',
                  background: (theme) =>
                    `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.98)}, ${alpha(
                      theme.palette.info.light,
                      0.08,
                    )})`,
                  '& .leaflet-container': {
                    height: '100%',
                    width: '100%',
                    backgroundColor: '#d8e8f6',
                  },
                }}
              >
                <MapContainer
                  center={MAP_DEFAULT_CENTER}
                  zoom={MAP_DEFAULT_ZOOM}
                  minZoom={3}
                  maxZoom={12}
                  scrollWheelZoom
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <CapabilityMapResizer selectedLocationKey={selectedLocationKey} />
                  <CapabilityMapViewport
                    bounds={capabilityMapBounds}
                    boundsSignature={capabilityMapBoundsSignature}
                    selectedLocation={selectedLocation}
                  />
                  <CapabilityMapMarkers
                    groups={capabilityMapGroups}
                    selectedLocationKey={selectedLocationKey}
                    onSelectLocation={setSelectedLocationKey}
                  />
                </MapContainer>

                <Box
                  sx={{
                    position: 'absolute',
                    top: { xs: 12, sm: 20 },
                    left: { xs: 12, sm: 20 },
                    right: { xs: 12, sm: 'auto' },
                    zIndex: 500,
                    maxWidth: { xs: 'none', sm: 320 },
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: 3,
                      border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                      bgcolor: (theme) => alpha(theme.palette.background.paper, 0.92),
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '14px',
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                          color: 'primary.main',
                        }}
                      >
                        <ExploreRoundedIcon fontSize="small" />
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Map Interactions
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Click a bubble to open the capabilities at that location.
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Box>

                {selectedLocation ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: { xs: 'auto', md: 0 },
                      right: 0,
                      bottom: 0,
                      left: { xs: 0, md: 'auto' },
                      width: { xs: '100%', md: 420 },
                      maxHeight: { xs: '72%', md: 'none' },
                      zIndex: 600,
                      bgcolor: (theme) => alpha(theme.palette.background.paper, 0.97),
                      backdropFilter: 'blur(14px)',
                      borderLeft: { xs: 0, md: '1px solid' },
                      borderTop: { xs: '1px solid', md: 0 },
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                      boxShadow: {
                        xs: '0 -16px 36px rgba(15, 23, 42, 0.16)',
                        md: '-16px 0 36px rgba(15, 23, 42, 0.16)',
                      },
                      overflowY: 'auto',
                    }}
                  >
                    <Stack spacing={2} sx={{ p: 2.5 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={2}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Capabilities At This Location
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatCoordinate(selectedLocation.latitude)},{' '}
                            {formatCoordinate(selectedLocation.longitude)}
                          </Typography>
                        </Box>
                        <IconButton
                          aria-label="Close location sidebar"
                          onClick={() => setSelectedLocationKey('')}
                          size="small"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Stack>

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip
                          color="primary"
                          label={`${selectedLocationCapabilityCount} capabilities`}
                        />
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setSelectedLocationKey('')}
                        >
                          Close panel
                        </Button>
                      </Stack>

                      {selectedLocation.capabilities.map((capability) => (
                        <CapabilityResultCard
                          key={capability.capability_definition_id}
                          item={capability}
                          onRequestTarget={handleRequestTarget}
                        />
                      ))}
                    </Stack>
                  </Box>
                ) : null}
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
